import type { ReactNode } from "react";
import { useEffect } from "react";

import { BuyJobDetail } from "./pages/BuyJobDetail";
import { GroupingReview } from "./pages/GroupingReview";
import { Items } from "./pages/Items";
import { Manage } from "./pages/Manage";
import { Now } from "./pages/now/Now";
import { ProductPage } from "./pages/product/ProductPage";
import { Settings } from "./pages/Settings";
import { StoreSettings } from "./pages/StoreSettings";
import { Watchlist } from "./pages/Watchlist";
import { matchPath, navigate } from "./router";

// The group page merged into ProductPage, which resolves a product id to its
// own group — no caller constructs a `/items/group/:groupKey` link anymore
// (checked: only this route matcher referenced it), so this only exists for
// old bookmarks/history. Navigating is a side effect, so it belongs in an
// effect rather than in renderRoute's body, which must stay a pure render.
function Redirect({ to }: { to: string }) {
	useEffect(() => {
		navigate(to);
	}, [to]);
	return null;
}

function NotFound({ path }: { path: string }) {
	return (
		<div className="mx-auto max-w-3xl px-4 py-12 text-center sm:px-6 sm:py-16">
			<h1 className="mb-2 text-[22px] font-bold font-tag">No such page</h1>
			<p className="text-[13px] text-muted">
				Nothing is routed at <code className="font-mono">{path}</code>. Pick a section from the navigation to carry on.
			</p>
		</div>
	);
}

export function renderRoute(path: string): ReactNode {
	if (path === "/" || path === "") return <Now />;

	// matchPath already decodeURIComponent's :params (router.ts:44), so do NOT
	// decode again here — the old App.tsx did, which double-decodes any group
	// key containing a literal '%'.
	if (matchPath(path, "/items/group/:groupKey")) return <Redirect to="/items" />;

	const item = matchPath(path, "/items/:id");
	if (item) return <ProductPage id={item.params.id!} />;
	if (matchPath(path, "/items")) return <Items />;

	const buy = matchPath(path, "/buy/:id");
	if (buy) return <BuyJobDetail id={buy.params.id!} />;

	if (matchPath(path, "/manage/stores")) return <StoreSettings />;
	if (matchPath(path, "/manage/watchlist")) return <Watchlist />;
	if (matchPath(path, "/manage/grouping")) return <GroupingReview />;
	if (matchPath(path, "/manage/settings")) return <Settings />;
	if (matchPath(path, "/manage")) return <Manage />;

	// Retired routes. matchPath is segment-count exact (router.ts:39), so
	// these cannot shadow the live routes above, and it already decodes
	// :params (router.ts:44) — do not decode again.
	if (matchPath(path, "/feed")) return <Redirect to="/" />;

	// Pre-redesign paths, still routable so old links and bookmarks work.
	if (matchPath(path, "/watchlist")) return <Watchlist />;
	if (matchPath(path, "/grouping")) return <GroupingReview />;
	if (matchPath(path, "/stores")) return <StoreSettings />;
	if (matchPath(path, "/settings")) return <Settings />;

	return <NotFound path={path} />;
}
